import React, { ReactElement, useMemo, useState } from 'react';
import { OptionTypeWithExtraMenuItemLabel } from '../../../Select/types';
import { SelectWithFilter } from '../../../Select/SelectWithFilter';
import { Button } from '../../../Button/Button';
import { useDataTableFilterContext } from '../../utils/DataTableFilterContext';
import { QueryModel } from '../../utils/DataTableQueryProvider/DataTableQuery.types';
import { DataTableFilterProps } from './DataTableFiltersChip';
import appTheme from '../../../../theme/appTheme';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';

type KeyOfQuery<Q> = keyof Q & string;

type Option<Q> = OptionTypeWithExtraMenuItemLabel<KeyOfQuery<Q>>;

interface Props<Q extends QueryModel> {
  filters: DataTableFilterProps<KeyOfQuery<Q>>[];
}

export const FilterPicker = <Q extends QueryModel>({ filters }: Props<Q>): ReactElement | null => {
  const { visibleFilters, setVisibleFilters } = useDataTableFilterContext();
  const showAllFilters = visibleFilters === 'all';
  const [showSelect, setShowSelect] = useState(false);
  const { intl } = useIntlUds();

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  const availableFilters: Option<Q>[] = useMemo(
    () =>
      filters
        .map((props) => ({
          label: props.label,
          value: props.id,
          group: props.group
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        }),
    []
  );

  const selectedOptions: string[] = visibleFilters === 'all' ? [] : visibleFilters;

  return (
    <>
      {!showAllFilters && !showSelect && (
        <Button
          fullWidth
          style={{ marginTop: appTheme.spacingSize.l }}
          color="primary"
          variant="outlined"
          startIcon="add"
          onClick={() => setShowSelect(true)}
          dataTest="data-table-add-filter"
        >
          {intl.DataTable.Filters.Current.addNewFilterBtn}
        </Button>
      )}
      {!showAllFilters && showSelect && (
        <SelectWithFilter<KeyOfQuery<Q>>
          fullWidth
          forceOpen
          multiple
          onClose={() => setShowSelect(false)}
          placeholder={intl.DataTable.Filters.Current.addNewFilterInput.placeholder}
          value={selectedOptions}
          required
          dataTest="data-table-select-filters"
          onChange={(options) => {
            if (options) {
              const selectedFilter = filters.filter((child) => options.find((option) => option === child.id));
              setVisibleFilters(selectedFilter.map((it) => it.id));
            }
          }}
          options={availableFilters}
        />
      )}
    </>
  );
};
